import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatDateTime } from '../utils/format';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';
import ExpandAllHeader from '../components/ExpandAllHeader';
import AutoRefreshControls from '../components/AutoRefreshControls';

// 主列顯示的欄位
const MAIN_FIELDS = ['date_event_id', 'name', 'home_team', 'away_team', 'start_time', 'method_type', 'created_at', 'updated_at'];

function MethodData({ token, backendUrl, dateRange, setDateRange, error, setError }) {
  const { t } = useTranslation();
  const [methodTypes, setMethodTypes] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const pagination = usePagination('method.pageSize', 50);
  const { handleSort, sortableHeader, sortData } = useTableSort(null, 'asc');

  // Fetch method types
  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.get(`${backendUrl}/method/types`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const types = res.data.data || [];
        setMethodTypes(types);
        if (types.length > 0) setSelectedMethod(types[0]);
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        setError(t('method.cannotFetchTypes'));
      }
    })();
    return () => controller.abort();
  }, [backendUrl, token, t, setError]);

  // Fetch data
  const fetchData = useCallback(async (currentToken, method, start, end, page, size, signal) => {
    if (!method) return;
    setLoading(true);
    setExpandedRows(new Set());
    try {
      const skip = (page - 1) * size;
      const baseUrl = `${backendUrl}/method/${method}`;
      const params = { start_date: start, end_date: end };

      let newTotalCount = 0;
      try {
        const countRes = await axios.get(`${baseUrl}/count`, {
          headers: { Authorization: `Bearer ${currentToken}` },
          params,
          signal,
        });
        newTotalCount = countRes.data.count || 0;
      } catch (e) {
        if (axios.isCancel(e) || e.name === 'CanceledError') return;
      }
      if (signal?.aborted) return;
      pagination.setTotalCount(newTotalCount);

      const res = await axios.get(baseUrl, {
        headers: { Authorization: `Bearer ${currentToken}` },
        params: { ...params, skip, limit: size },
        signal,
      });
      if (signal?.aborted) return;

      const resultData = res.data.data ? res.data.data : (Array.isArray(res.data) ? res.data : []);
      if (res.data.error) {
        setError(res.data.error);
        setData([]);
      } else {
        setData(resultData);
        setError(null);
      }
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setError(err.response?.data?.detail || err.message);
      setData([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [backendUrl, pagination, setError]);

  useEffect(() => {
    if (!(token && selectedMethod && dateRange.start_date && dateRange.end_date)) return undefined;
    const controller = new AbortController();
    fetchData(token, selectedMethod, dateRange.start_date, dateRange.end_date, pagination.currentPage, pagination.pageSize, controller.signal);
    return () => controller.abort();
  }, [token, selectedMethod, dateRange, pagination.currentPage, pagination.pageSize, fetchData]);

  const autoRefresh = useAutoRefresh(
    (signal) => fetchData(token, selectedMethod, dateRange.start_date, dateRange.end_date, pagination.currentPage, pagination.pageSize, signal),
    { defaultInterval: 60, defaultEnabled: true },
  );

  const handleApply = () => {
    pagination.resetPage();
    setError(null);
    fetchData(token, selectedMethod, dateRange.start_date, dateRange.end_date, 1, pagination.pageSize);
  };

  const handleMethodChange = (e) => {
    setSelectedMethod(e.target.value);
    pagination.resetPage();
    handleSort(null);
  };

  const toggleExpand = (idx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const sortedData = sortData(data, ['start_time', 'created_at', 'updated_at']);

  const allExpanded = expandedRows.size === sortedData.length && sortedData.length > 0;

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(sortedData.map((_, idx) => idx)));
    }
  };

  // 取得展開區域要顯示的欄位（排除主列欄位）
  const getDetailFields = (row) => {
    return Object.keys(row).filter((k) => !MAIN_FIELDS.includes(k) && k !== 'url' && k !== '_id');
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('method.title')}</h2>
        <AutoRefreshControls {...autoRefresh} />
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label">{t('method.selectType')}</label>
          <select className="form-select" value={selectedMethod} onChange={handleMethodChange}>
            {methodTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <label className="form-label">{t('common.startDate')}</label>
          <input type="date" className="form-control" value={dateRange.start_date}
            onChange={(e) => setDateRange((prev) => ({ ...prev, start_date: e.target.value }))} />
        </div>
        <div className="col-auto">
          <label className="form-label">{t('common.endDate')}</label>
          <input type="date" className="form-control" value={dateRange.end_date}
            onChange={(e) => setDateRange((prev) => ({ ...prev, end_date: e.target.value }))} />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={handleApply} disabled={loading}>{t('common.apply')}</button>
        </div>
      </div>

      <p className="text-muted small mb-3">
        {t('method.currentType')}: {selectedMethod} | {t('method.totalCount')}: {pagination.totalCount} | {t('method.showing')}: {data.length} {t('common.items')}
      </p>

      <PaginationControls {...pagination} />

      <ErrorLoading error={error} loading={loading} />

      {!loading && sortedData.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-bordered table-hover table-sm">
            <thead className="table-secondary">
              <tr>
                <ExpandAllHeader allExpanded={allExpanded} onToggle={toggleExpandAll} />
                {sortableHeader('Event ID', 'date_event_id')}
                {sortableHeader('Name', 'name')}
                {sortableHeader('Home', 'home_team')}
                {sortableHeader('Away', 'away_team')}
                {sortableHeader('Start Time', 'start_time')}
                {sortableHeader('Type', 'method_type')}
                {sortableHeader('Created', 'created_at')}
                {sortableHeader('Updated', 'updated_at')}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => {
                const isExpanded = expandedRows.has(idx);
                const detailFields = getDetailFields(row);
                return (
                  <>
                    <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => toggleExpand(idx)}
                        className={isExpanded ? 'table-active' : ''}>
                      <td className="text-center">
                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      </td>
                      <td>
                        {row.url ? (
                          <a href={row.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                            {row.date_event_id}
                          </a>
                        ) : row.date_event_id}
                      </td>
                      <td>{row.name}</td>
                      <td>{row.home_team}</td>
                      <td>{row.away_team}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.start_time)}</td>
                      <td><span className="badge bg-info">{row.method_type}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.updated_at)}</td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${idx}-detail`} style={{ '--bs-table-bg': 'var(--bs-tertiary-bg)' }}>
                        <td></td>
                        <td colSpan={8}>
                          <div className="row g-2" style={{ fontSize: '0.85rem' }}>
                            {detailFields.map((key) => (
                              <div className="col-md-3 col-sm-6" key={key}>
                                <strong className="text-body-secondary">{key}:</strong>{' '}
                                <span>{formatDateTime(row[key])}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && <div className="text-center text-muted my-5">{t('common.noData')}</div>
      )}
    </div>
  );
}

export default MethodData;
